import { useRolodexEdit } from 'features/fde/rolodexAPI'
import isNil from 'lodash/isNil'
import { FC, useCallback } from 'react'
import { GeckoSite } from 'types'

import { MapboxDrawMode } from './useMapboxDraw'

interface SiteBoundsEditorActionButtonsProps {
  canSave: boolean
  setCanSave: (newCanSave: boolean) => void
  site: GeckoSite
  setSite: (newSite: GeckoSite) => void
  getDrawnGeometry: () => GeoJSON.Geometry | null
  clearDrawings: () => void
  drawMode: MapboxDrawMode
  setDrawMode: (newDrawMode: MapboxDrawMode) => void
  uploadMapGeometry: (geo: GeoJSON.Geometry) => void
}

export const SiteBoundsEditorActionButtons: FC<
  SiteBoundsEditorActionButtonsProps
> = ({
  site,
  setSite,
  canSave,
  setCanSave,
  drawMode,
  setDrawMode,
  getDrawnGeometry,
  clearDrawings,
  uploadMapGeometry,
}) => {
  const { rolodexPatch } = useRolodexEdit()
  const handleSave = useCallback(() => {
    const geometry = getDrawnGeometry()
    if (geometry) {
      rolodexPatch([
        {
          id: site.id,
          data: {
            geography: geometry,
          },
        },
      ]).then((res) => {
        if (res.status >= 300) {
          alert(`Failed to save site bounds: ${JSON.stringify(res)}`)
          return
        }
        setCanSave(false)
        clearDrawings()
        const newSite = {
          ...site,
          data: {
            ...site.data,
            geography: geometry,
          },
        }
        setSite(newSite)
      })
    }

    setDrawMode(MapboxDrawMode.SIMPLE_SELECT)
  }, [
    getDrawnGeometry,
    setDrawMode,
    rolodexPatch,
    site,
    setCanSave,
    clearDrawings,
    setSite,
  ])

  const handleCancel = useCallback(() => {
    clearDrawings()
    setCanSave(false)
    setDrawMode(MapboxDrawMode.SIMPLE_SELECT)
  }, [clearDrawings, setCanSave, setDrawMode])

  const handleStartEdit = useCallback(() => {
    clearDrawings()

    if (site.data.geography) {
      uploadMapGeometry(site.data.geography)
    }

    setDrawMode(MapboxDrawMode.DRAW_POLYGON)
  }, [clearDrawings, site.data.geography, setDrawMode, uploadMapGeometry])

  if (drawMode === MapboxDrawMode.SIMPLE_SELECT) {
    // Not in edit mode
    return (
      <button onClick={handleStartEdit}>
        {isNil(site.data.geography) ? 'Draw' : 'Update'} Site Bounds
      </button>
    )
  }
  // In edit mode (draw mode === draw polygon)

  return (
    <>
      {canSave && <button onClick={handleSave}>Save</button>}
      <button onClick={handleCancel}>Cancel</button>
    </>
  )
}
